/**
 * Dashboard Page
 * ===============
 * Main dashboard with summary statistics and quick actions.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  FileText,
  Calendar,
  TrendingUp,
  AlertTriangle,
  Plus,
  ArrowRight,
  Clock,
} from 'lucide-react';

import { apiClient } from '@/api/client';
import { useUser } from '@/hooks/useAuth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatDate } from '@/lib/utils';
import { PatientSearchModal } from '@/components/PatientSearchModal';
import { useCreateChart } from '@/hooks/useChart';
import type { PatientBrief } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

interface DashboardStats {
  patients: {
    total: number;
    active: number;
  };
  charts: {
    total: number;
    finalized: number;
    drafts: number;
  };
  users: {
    active: number;
  };
}

interface RecentChart {
  id: string;
  patient_name: string;
  exam_date: string;
  status: string;
}

interface PatientNeedingExam {
  id: string;
  full_name: string;
  last_exam_date: string | null;
  days_since_exam: number;
}

// =============================================================================
// QUERIES
// =============================================================================

async function fetchDashboardStats(): Promise<DashboardStats> {
  const response = await apiClient.get('/admin/stats');
  return response.data;
}

async function fetchRecentCharts(): Promise<RecentChart[]> {
  const response = await apiClient.get('/charts', {
    params: { page_size: 5, sort_order: 'desc' },
  });
  return response.data.items;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function Dashboard() {
  const user = useUser();
  const navigate = useNavigate();
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const createChartMutation = useCreateChart();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: fetchDashboardStats,
  });

  const { data: recentCharts, isLoading: chartsLoading } = useQuery({
    queryKey: ['dashboard', 'recent-charts'],
    queryFn: fetchRecentCharts,
  });

  // Debug: Log user and auth state on mount
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Dashboard user:', user);
    // eslint-disable-next-line no-console
    console.log('Dashboard authStore:', window.localStorage.getItem('perio-chart-auth'));
    if (!user) {
      // eslint-disable-next-line no-console
      console.warn('No user found in useUser(), redirecting to login');
    }
  }, [user]);

  const handleSelectPatient = async (patient: PatientBrief) => {
    try {
      const newChart = await createChartMutation.mutateAsync({
        patient_id: patient.id,
        exam_date: new Date().toISOString().split('T')[0],
      });
      setIsPatientModalOpen(false);
      navigate(`/charts/${newChart.id}`);
    } catch (error) {
      console.error('Failed to create chart:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.first_name || 'User'}
          </h1>
          <p className="text-gray-600">
            Here's what's happening with your practice today.
          </p>
        </div>
        <Button onClick={() => setIsPatientModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Chart
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={stats?.patients.total ?? '—'}
          subValue={`${stats?.patients.active ?? 0} active`}
          icon={<Users className="w-6 h-6" />}
          color="blue"
          loading={statsLoading}
        />
        <StatCard
          title="Total Charts"
          value={stats?.charts.total ?? '—'}
          subValue={`${stats?.charts.finalized ?? 0} finalized`}
          icon={<FileText className="w-6 h-6" />}
          color="green"
          loading={statsLoading}
        />
        <StatCard
          title="Draft Charts"
          value={stats?.charts.drafts ?? '—'}
          subValue="Pending completion"
          icon={<Clock className="w-6 h-6" />}
          color="yellow"
          loading={statsLoading}
        />
        <StatCard
          title="Active Users"
          value={stats?.users.active ?? '—'}
          subValue="In your organization"
          icon={<TrendingUp className="w-6 h-6" />}
          color="purple"
          loading={statsLoading}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Charts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Charts
            </h2>
            <Link
              to="/charts"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          {chartsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-center space-x-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentCharts && recentCharts.length > 0 ? (
            <div className="space-y-3">
              {recentCharts.map((chart) => (
                <Link
                  key={chart.id}
                  to={`/charts/${chart.id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {chart.patient_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(chart.exam_date)}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={chart.status === 'finalized' ? 'success' : 'warning'}
                  >
                    {chart.status}
                  </Badge>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No charts yet</p>
              <button
                onClick={() => setIsPatientModalOpen(true)}
                className="text-primary-600 hover:text-primary-700"
              >
                Create your first chart
              </button>
            </div>
          )}
        </Card>

        {/* Quick Actions */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <QuickAction
              to="/patients/add"
              icon={<Users className="w-6 h-6" />}
              title="Add Patient"
              description="Register a new patient"
            />
            <QuickActionButton
              onClick={() => setIsPatientModalOpen(true)}
              icon={<FileText className="w-6 h-6" />}
              title="New Chart"
              description="Start charting"
            />
            <QuickAction
              to="/patients"
              icon={<Calendar className="w-6 h-6" />}
              title="View Patients"
              description="Patient list"
            />
            <QuickAction
              to="/settings"
              icon={<TrendingUp className="w-6 h-6" />}
              title="Reports"
              description="View analytics"
            />
          </div>
        </Card>
      </div>

      {/* Patient Search Modal */}
      <PatientSearchModal
        isOpen={isPatientModalOpen}
        onClose={() => setIsPatientModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        isLoading={createChartMutation.isPending}
      />
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface StatCardProps {
  title: string;
  value: number | string;
  subValue: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'purple';
  loading?: boolean;
}

function StatCard({ title, value, subValue, icon, color, loading }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {loading ? (
            <div className="animate-pulse h-8 w-16 bg-gray-200 rounded mt-1" />
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">{subValue}</p>
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

interface QuickActionProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function QuickAction({ to, icon, title, description }: QuickActionProps) {
  return (
    <Link
      to={to}
      className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
    >
      <div className="text-primary-600 mb-2">{icon}</div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </Link>
  );
}

interface QuickActionButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function QuickActionButton({ onClick, icon, title, description }: QuickActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
    >
      <div className="text-primary-600 mb-2">{icon}</div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </button>
  );
}
